import { useEffect, useRef, useState } from "react";
import { HiArrowSmLeft } from "react-icons/hi";
import { httpService } from "../../../../../src/services/httpService";
import { FiPhone, FiUser, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { MdHistory, MdLocalHotel, MdShoppingCart, MdPets, MdPayment } from "react-icons/md";
import { FaRuler } from "react-icons/fa";
import AddAppointment from "../appointmentcalendar/AddAppointment";

const CombinedHistoryDrawer = ({
    isOpen = false,
    onClose = () => { },
    clientId = null,
    clientData = null,
    onReorder = null,
}) => {
    const [orders, setOrders] = useState([]); // Combined orders with their items
    const [loading, setLoading] = useState(false);
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [showAddAppointment, setShowAddAppointment] = useState(false);
    const [selectedBookingForReorder, setSelectedBookingForReorder] = useState(null);

    useEffect(() => {
        if (isOpen && clientId) {
            fetchHistory();
        }
        if (!isOpen) {
            setExpandedOrder(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, clientId]);

    // Auto-expand first order when data loads
    useEffect(() => {
        if (isOpen && orders.length > 0 && !expandedOrder) {
            setExpandedOrder(orders[0]._id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orders, isOpen]);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const res = await httpService.get(`/vendorbooking/bookingHistory/${clientId}`);
            
            if (res.data && Array.isArray(res.data)) {
                // Transform orders to match enquiry-like structure
                const transformedOrders = res.data.map(order => ({
                    ...order,
                    // Ensure order has proper amount fields
                    total_amount: order.total_amount || 0,
                    total_paid_amount: order.total_paid_amount || 0,
                    total_gst: order.total_gst || 0,
                    total_discount: order.total_discount || 0,
                    // Transform order_items to items for consistency with enquiry structure
                    items: (order.order_items || []).map(item => ({
                        ...item,
                        // Map item fields to match enquiry item structure
                        _id: item._id,
                        type: item.item_type,
                        service_name: item.service_name || "Service",
                        pet_name: item.pet_name,
                        pet_breed: item.pet_breed,
                        pet_size: item.pet_size,
                        start_date: item.start_date,
                        end_date: item.end_date,
                        time_slot: item.start_time,
                        duration: item.duration,
                        quantity: item.quantity,
                        unit_price: item.unit_price,
                        total_price: item.total_price,
                        price: item.price || item.total_price,
                        employee_name: item.employee_name,
                        room_name: item.room_name,
                        room_number: item.room_number,
                        sku_number: item.sku_number,
                        addons: item.addons || [],
                        status: item.status,
                        payment_status: order.payment_status,
                        // Additional fields for display
                        service_type_name: item.service_type_name,
                        roomTypeName: item.roomTypeName,
                    }))
                }));

                setOrders(transformedOrders);
            } else {
                setOrders([]);
            }
        } catch (error) {
            console.error("Error fetching history:", error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "completed") return "bg-emerald-200 text-emerald-800";
        if (s === "confirmed") return "bg-green-200 text-green-800";
        if (s === "booked") return "bg-blue-200 text-blue-800";
        if (s === "cancel" || s === "cancelled") return "bg-red-200 text-red-800";
        if (s === "no show" || s === "no_show") return "bg-purple-200 text-purple-800";
        if (s === "draft") return "bg-gray-200 text-gray-800";
        if (s === "arrived") return "bg-indigo-200 text-indigo-800";
        if (s === "partial") return "bg-yellow-200 text-yellow-800";
        return "bg-gray-200 text-gray-800";
    };

    const getPaymentStatusBadge = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "paid") return "bg-green-200 text-green-800";
        if (s === "unpaid") return "bg-red-200 text-red-800";
        if (s === "partial") return "bg-yellow-200 text-yellow-800";
        return "bg-gray-200 text-gray-800";
    };

    const formatDate = (d) => {
        if (!d) return "";
        try {
            return new Date(d).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        } catch (_) {
            return d;
        }
    };

    const getServiceIcon = (type) => {
        const t = (type || "").toLowerCase();
        if (t === "product") return <MdShoppingCart className="w-4 h-4 text-green-600" />;
        if (t === "boarding" || t === "day_care" || t === "daycare") return <MdLocalHotel className="w-4 h-4 text-orange-600" />;
        return <MdPets className="w-4 h-4 text-orange-600" />;
    };

    const getServiceIconBg = (type) => {
        const t = (type || "").toLowerCase();
        if (t === "product") return "bg-green-100";
        if (t === "boarding" || t === "day_care" || t === "daycare") return "bg-orange-100";
        return "bg-orange-100";
    };

    const handleReorder = (item) => {
        let appointmentType = "Appointment";
        if (
            item.type === "boarding" ||
            item.item_type === "boarding" ||
            item.type === "day_care" ||
            item.item_type === "day_care" ||
            item.type === "daycare" ||
            item.item_type === "daycare"
        ) {
            appointmentType = "Boarding";
        }

        const prepared = {
            ...item,
            type: appointmentType,
            service_id: item.service_id,
            vendor_pet_id: item.vendor_pet_id || item.pet_id,
            pet_id: item.vendor_pet_id || item.pet_id,
            service_type_name: item.service_type_name || item.service_name || "Service",
            service_category_type_id: item.service_category_type_id || item.service_type_id,
            service_type_id: item.service_type_id || item.service_category_type_id,
            item_type: item.type || item.item_type,
            vendor_client: clientData,
            client: clientData,
        };

        if (onReorder) onReorder(prepared);

        setSelectedBookingForReorder(prepared);
        setShowAddAppointment(true);
    };

    const handleAddAppointmentClose = () => {
        setShowAddAppointment(false);
        setSelectedBookingForReorder(null);
    };

    const handleAddAppointmentSave = (data) => {
        console.log("Appointment saved from reorder:", data);
        setShowAddAppointment(false);
        setSelectedBookingForReorder(null);
    };

    return (
        <div
            className={`fixed inset-0 z-50 transition-all duration-300 ease-in-out ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
        >
            <div className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]" onClick={onClose} />
            <div
                className={`absolute right-0 top-0 h-full w-full lg:w-2/5 bg-white shadow-xl transform transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-start items-center p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm gap-3">
                    <button
                        onClick={onClose}
                        className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/70 transition-colors duration-200"
                    >
                        <HiArrowSmLeft className="w-6 h-6 text-gray-700" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                            <MdHistory className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">Order History</h2>
                    </div>
                </div>

                {/* Content */}
                <div className="flex h-[calc(100vh-73px)] flex-col">
                    <div className="p-6 space-y-6 overflow-auto bg-gradient-to-b from-gray-50 to-white flex-1">
                        {/* Client Info */}
                        {clientData && (
                            <div className="bg-gradient-to-r from-white to-blue-50 rounded-xl border border-blue-100 p-4 shadow-sm">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                        <FiUser className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">
                                            {clientData.firstname} {clientData.lastname}
                                        </h3>
                                        <p className="text-sm text-gray-600 flex items-center gap-1">
                                            <FiPhone className="w-3 h-3" />
                                            {clientData.phone}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* LOADING */}
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                                    <p className="mt-4 text-gray-600">Loading history...</p>
                                </div>
                            </div>
                        ) : orders.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <MdHistory className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-gray-600 font-medium">No order history found</p>
                                <p className="text-sm text-gray-500 mt-1">This client hasn't made any orders yet.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {orders.map((order, index) => (
                                    <div
                                        key={order._id || index}
                                        className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-all duration-200"
                                    >
                                        {/* Order Header - Make entire header clickable */}
                                        <div 
                                            className="p-4 bg-gradient-to-r from-blue-400 to-indigo-600 text-white cursor-pointer"
                                            onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                                        >
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-4">
                                                    <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-lg text-sm font-bold">
                                                        #{order.order_number}
                                                    </div>
                                                    <div className="text-sm">
                                                        {formatDate(order.createdAt)}
                                                    </div>
                                                    <div className="px-3 py-1 rounded-lg text-sm font-bold bg-white/20 backdrop-blur-sm">
                                                        ₹ {Math.round(Number(order.total_amount || 0))}
                                                    </div>
                                                    {order.payment_status && (
                                                        <div className={`px-2 py-1 rounded-lg text-xs font-medium ${getPaymentStatusBadge(order.payment_status)}`}>
                                                            {(order.payment_status || "").charAt(0).toUpperCase() + (order.payment_status || "").slice(1)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {expandedOrder === order._id ? (
                                                        <FiChevronUp className="w-5 h-5 text-white" />
                                                    ) : (
                                                        <FiChevronDown className="w-5 h-5 text-white" />
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Expandable Content */}
                                        {expandedOrder === order._id && (
                                            <div className="p-4 space-y-4 bg-gray-50">
                                                {/* Order Items */}
                                                {order.items && order.items.map((item, itemIndex) => (
                                                    <div key={item._id || itemIndex} className="bg-white rounded-lg p-4 shadow-sm">
                                                        {/* Date Header */}
                                                        <div className="flex justify-between items-center mb-3">
                                                            <div className="text-sm font-medium text-gray-700">
                                                                {item.type === "product" ? (
                                                                    "Product"
                                                                ) : (
                                                                    <>
                                                                        {(item.start_date || item.appointment_date) && formatDate(
                                                                            item.start_date || item.appointment_date
                                                                        )}
                                                                        {(item.start_date || item.appointment_date) && item.time_slot && " - "}
                                                                        {item.time_slot && item.time_slot}
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {item.type !== "product" && (
                                                                    <button
                                                                        onClick={() => handleReorder(item)}
                                                                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-200"
                                                                    >
                                                                        Reorder
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Main Service/Product */}
                                                        <div className="flex items-start gap-3 mb-3">
                                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${getServiceIconBg(item.type)}`}>
                                                                {getServiceIcon(item.type)}
                                                            </div>
                                                            <div className="flex-1">
                                                                {item.type === "product" ? (
                                                                    // Product display
                                                                    <>
                                                                        <div className="flex items-center gap-2 mb-1">
                                                                            <span className="font-semibold text-gray-900">
                                                                                {item.service_name}
                                                                            </span>
                                                                            {item.sku_number && (
                                                                                <>
                                                                                    <span className="text-gray-400">•</span>
                                                                                    <span className="text-sm text-gray-600">
                                                                                        SKU: {item.sku_number}
                                                                                    </span>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-gray-900 font-medium">
                                                                                Product
                                                                            </span>
                                                                            <span className="text-green-600 font-bold">
                                                                                ₹ {Math.round(Number(item.total_price || item.price || 0))}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex items-center justify-between mt-1">
                                                                            <p className="text-sm text-gray-500">
                                                                                Quantity: {item.quantity || 1} × ₹{Math.round(Number(item.unit_price || item.price || 0))}
                                                                            </p>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    // Service display section - replace the existing service display code with this:
                                                                    <>
                                                                        <div className="flex items-center gap-2 mb-1">
                                                                            <span className="font-semibold text-gray-900">
                                                                                {item.pet_name}
                                                                            </span>
                                                                            {item.pet_breed && item.pet_breed.trim() !== "" && (
                                                                                <>
                                                                                    <span className="text-gray-400">•</span>
                                                                                    <span className="text-sm text-gray-600">
                                                                                        {item.pet_breed}
                                                                                    </span>
                                                                                </>
                                                                            )}
                                                                            {item.pet_size && item.pet_size.trim() !== "" && (
                                                                                <>
                                                                                    <span className="text-gray-400">•</span>
                                                                                    <span className="text-sm text-gray-600 inline-flex items-center gap-1">
                                                                                        <FaRuler className="w-3.5 h-3.5 text-gray-400" />
                                                                                        {item.pet_size}
                                                                                    </span>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                        
                                                                        {/* First line: Check-in/Check-out for boarding, Time for appointments */}
                                                                        <div className="text-sm text-gray-700 mb-1">
                                                                            {item.type === "boarding" ? (
                                                                                <>
                                                                                    {item.start_date && `Check-in: ${formatDate(item.start_date)}`}
                                                                                    {item.start_date && item.end_date && ` • Check-out: ${formatDate(item.end_date)}`}
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    {(item.start_date || item.appointment_date) && formatDate(item.start_date || item.appointment_date)}
                                                                                    {(item.start_date || item.appointment_date) && item.time_slot && " - "}
                                                                                    {item.time_slot && item.time_slot}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                        
                                                                        {/* Second line: Service details */}
                                                                        <div className="flex items-center justify-between">
                                                                            <div className="flex-1">
                                                                                <span className="text-gray-900 font-medium block mb-1">
                                                                                    {item.service_name || "Service"}
                                                                                </span>
                                                                                
                                                                                {/* Third line: Additional details */}
                                                                                <div className="text-sm text-gray-500">
                                                                                    {item.type === "boarding" ? (
                                                                                        <>
                                                                                            {item.duration && `${item.duration}`}
                                                                                            {item.duration && item.room_name && " • "}
                                                                                            {item.room_name && `Room: ${item.room_name}`}
                                                                                            {item.room_number && ` – ${item.room_number}`}
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            {item.time_slot && item.time_slot}
                                                                                            {item.time_slot && item.duration && " • "}
                                                                                            {item.duration && `${item.duration}`}
                                                                                            {(item.time_slot || item.duration) && item.employee_name && " • "}
                                                                                            {item.employee_name && item.employee_name}
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            <span className="text-green-600 font-bold whitespace-nowrap ml-4">
                                                                                ₹ {Math.round(Number(item.total_price || item.price || 0))}
                                                                            </span>
                                                                        </div>

                                                                        {item.status && (
                                                                            <div className={`inline-flex px-2 py-0.5 rounded text-xs font-medium mt-2 ${getStatusBadge(item.status)}`}>
                                                                                {item.status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Add-ons */}
                                                        {item.addons && item.addons.length > 0 && (
                                                            <div className="ml-11 space-y-2">
                                                                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                                    <span className="text-xs font-bold text-purple-600">✓</span>
                                                                    Add-ons
                                                                </h4>
                                                                {item.addons.map((addon, addonIndex) => (
                                                                    <div key={addonIndex} className="flex justify-between items-center text-sm bg-purple-50 p-2 rounded">
                                                                        <span className="text-gray-700">{addon.name}</span>
                                                                        <span className="text-green-600 font-medium">₹ {Math.round(Number(addon.addon_price || 0))}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}

                                                {/* Totals Section */}
                                                <div className="bg-white rounded-lg p-4 shadow-sm">
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between text-sm">
                                                            <span>Subtotal</span>
                                                            <span>
                                                                ₹ {Math.round(Number(order.total_amount || 0) - Number(order.total_gst || 0))}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between text-sm">
                                                            <span>GST</span>
                                                            <span>
                                                                ₹ {Math.round(Number(order.total_gst || 0))}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between text-sm">
                                                            <span>Discount</span>
                                                            <span>
                                                                ₹ {Math.round(Number(order.total_discount || 0))}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2">
                                                            <span>Total Amount</span>
                                                            <span>
                                                                ₹ {Math.round(Number(order.total_amount || 0))}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between text-sm pt-2">
                                                            <span>Paid Amount</span>
                                                            <span className={`font-medium ${order.payment_status === 'paid' ? 'text-green-600' : order.payment_status === 'unpaid' ? 'text-red-600' : 'text-yellow-600'}`}>
                                                                ₹ {Math.round(Number(order.total_paid_amount || 0))}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AddAppointment
                isOpen={showAddAppointment}
                onClose={handleAddAppointmentClose}
                selectedSlotInfo={null}
                clearSlotSelection={() => { }}
                editMode={false}
                editingAppointment={selectedBookingForReorder}
                onSave={handleAddAppointmentSave}
                appointmentStatusRef={null}
            />
        </div>
    );
};

export default CombinedHistoryDrawer;